// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, shorthands } from '@fluentui/react-components';
import React, { useState } from 'react';
import { Citation, IChatMessage } from '../../../libs/models/ChatMessage';
import { customTokens } from '../../../styles';
import { CitationCard } from '../shared/CitationCard';
import DocumentViewer from '../shared/DocumentViewer';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        ...shorthands.gap(customTokens.spacingVerticalS),
        flexDirection: 'column',
    },
    card: {
        display: 'flex',
        width: '100%',
        height: 'fit-content',
    },
});

interface ICitationCardsProps {
    message: IChatMessage;
}

export interface CitationMetadata {
    sourceName: string;
    documentId: string;
}

export type GroupedCitations = Array<[string, Citation[]]>;
type CitationsUsed = Record<string, number>;

export const CitationCards: React.FC<ICitationCardsProps> = ({ message }) => {
    const classes = useClasses();
    // const [showSnippetStates, setShowSnippetStates] = useState<boolean[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<{
        id: string;
        isOpen: boolean;
        initialPage?: number;
    }>({
        id: '',
        isOpen: false,
    });


    const citationsColours = message.citationsColours;

    const transformedColors = Object.entries(citationsColours ?? {}).reduce<
        Record<string, { red: number; green: number; blue: number }>
    >((acc, [key, value]) => {
        acc[key] = {
            red: Math.round(value.red === 0 ? value.red * 256 : value.red * 256 - 1),
            green: Math.round(value.green === 0 ? value.green * 256 : value.green * 256 - 1),
            blue: Math.round(value.blue === 0 ? value.blue * 256 : value.blue * 256 - 1),
        };
        return acc;
    }, {});

    if (!message.citations || message.citations.length === 0 || !message.citationsUsed) {
        return null;
    }

    const filteredCitations = message.citations.filter(
        (citation) => message.citationsUsed?.[citation.citationRef] !== undefined,
    );

    if (filteredCitations.length === 0) {
        return null;
    }

   const groupUsedCitationsBySourceWithColors = (
       citations: Citation[],
       citationsUsed: CitationsUsed,
       transformedColors: Record<string, { red: number; green: number; blue: number }>,
   ): Array<[CitationMetadata, Citation[]]> => {
       const usedCitations = citations.filter((citation) => citation.citationRef in citationsUsed);
       const groups = usedCitations.reduce<Record<string, [CitationMetadata, Citation[]]>>((acc, citation) => {
           const { citationRef, sourceName, documentId } = citation;

           const citationColor = transformedColors[citationRef];
           const citationWithColor = { ...citation, color: citationColor };

           if (!(sourceName in acc)) {
               acc[sourceName] = [{ sourceName, documentId }, []];
           }
           acc[sourceName][1].push(citationWithColor);

           return acc;
       }, {});

       return Object.entries(groups).map(([_, value]) => value);
   };

    const groupedCitationsWithColors = groupUsedCitationsBySourceWithColors(
        message.citations,
        message.citationsUsed,
        transformedColors,
    );

    return (
        <div className={classes.root}>
            {groupedCitationsWithColors.map(([documentGroup, groupedCitations]) => (
                <div key={`source-group-${documentGroup.sourceName}`} className="citation-group">
                    <h3 className="source-name">{documentGroup.sourceName}</h3>
                    <div className="citations-container">
                        {groupedCitations.map((citation, index) => (
                            <CitationCard
                                key={`citation-card-${documentGroup.sourceName}-${index}`}
                                citation={citation}
                                onClick={() => {
                                    const page = message.citationsUsed?.[citation.citationRef];
                                    setSelectedDocument({
                                        id: citation.documentId,
                                        isOpen: true,
                                        initialPage: page,
                                    });
                                }}
                            />
                        ))}
                    </div>
                </div>
            ))}

            <DocumentViewer
                documentId={selectedDocument.id}
                chatId={message.chatId}
                isOpen={selectedDocument.isOpen}
                onClose={() => {
                    setSelectedDocument({ id: '', isOpen: false });
                }}
                message={message}
                initialPage={selectedDocument.initialPage}
                groupedCitations={groupedCitationsWithColors.filter(
                    ([metadata, _]) => metadata.documentId === selectedDocument.id,
                )}
            />
        </div>
    );
};
